import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { questionsAPI } from "../services/apiService";
import { useSEO } from "../hooks/useSEO";
import { initializeTracking, trackPasswordVerification } from "../utils/trackingUtils";

const HollandCodes = () => {
  // Use SEO hook for Holland Codes page
  const { seoData } = useSEO("holland", {
    h1: "Holland Code - Tìm kiếm nghề nghiệp phù hợp",
    metaDescription:
      "Khám phá sở thích nghề nghiệp qua test Holland Code với 12 hoạt động. Tìm ra con đường sự nghiệp phù hợp nhất.",
    title: "Test Holland Code - Tìm kiếm nghề nghiệp phù hợp",
  });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState("intro"); // intro, test, completed, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null); // No time limit for Holland Codes test
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [hollandResult, setHollandResult] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reviewLaterQuestions, setReviewLaterQuestions] = useState([]);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuestionBoard, setShowQuestionBoard] = useState(false);
  const [showNeedAnswerNotification, setShowNeedAnswerNotification] =
    useState(false);
  const [isReviewingMode, setIsReviewingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load mật khẩu từ localStorage hoặc sử dụng mặc định
  const [correctPassword, setCorrectPassword] = useState("holland2025");
  
  // Load password from localStorage
  useEffect(() => {
    const savedTestPasswords = localStorage.getItem("test_passwords");
    if (savedTestPasswords) {
      const passwords = JSON.parse(savedTestPasswords);
      setCorrectPassword(passwords.holland || "holland2025");
    }
  }, []);

  // Load questions from API database
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        console.log("🔍 Loading Holland questions from API...");
        
        const apiQuestions = await questionsAPI.getByType("holland");
        console.log(
          `✅ Loaded ${apiQuestions.length} Holland questions from database`
        );
        
        if (apiQuestions && apiQuestions.length > 0) {
          setQuestions(apiQuestions);
        } else {
          console.warn(
            "❌ No Holland questions found in database, loading defaults..."
          );
          // Fallback to default hardcoded questions
          setQuestions(getDefaultQuestions());
        }
      } catch (error) {
        console.error("❌ Error loading Holland questions:", error);
        // Fallback to default questions
        setQuestions(getDefaultQuestions());
        console.log(
          `⚠️ Using fallback: ${getDefaultQuestions().length} default questions`
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    initializeTracking('holland');
  }, []);

  // Default questions fallback function
  const getDefaultQuestions = () => {
    return [
      {
        id: 1,
        activity: "Làm việc với động vật",
        type: "Realistic",
      },
      {
        id: 2,
        activity: "Nghiên cứu khoa học",
        type: "Investigative",
      },
      // ... More default questions for fallback
    ];
  };

  // Timer countdown
  useEffect(() => {
    if (currentScreen === "test" && timeLeft !== null && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentScreen === "test") {
      calculateResult();
    }
  }, [timeLeft, currentScreen]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNeedAnswerNotification) {
      const timer = setTimeout(() => {
        setShowNeedAnswerNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNeedAnswerNotification]);

  const formatTime = (seconds) => {
    if (seconds === null) return "Không giới hạn";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startTest = () => {
    setCurrentScreen("test");
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(null); // No time limit for Holland Codes test
    setIsReviewingMode(false); // Reset reviewing mode when starting test
    setReviewLaterQuestions([]); // Clear review later questions
    // Scroll to top when starting test
    window.scrollTo(0, 0);
  };

  const handleAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to next question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to previous question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const toggleReviewLater = () => {
    const newReviewLater = [...reviewLaterQuestions];
    const wasMarkedForReview = newReviewLater.includes(currentQuestion);
    
    if (wasMarkedForReview) {
      // Remove from review list (bỏ đánh dấu)
      newReviewLater.splice(newReviewLater.indexOf(currentQuestion), 1);
    } else {
      // Add to review list (đánh dấu)
      newReviewLater.push(currentQuestion);
    }
    setReviewLaterQuestions(newReviewLater);
    
    // Handle reviewing mode logic
    if (isReviewingMode && wasMarkedForReview) {
      // If we're in reviewing mode and user clicked "Bỏ đánh dấu"
      // Navigate to next purple question (same as when selecting answer)
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(
            currentQuestion,
            newReviewLater
          );
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode or marking for review - show notification and stay at current question
      setShowNeedAnswerNotification(true);
      
      // Exit reviewing mode when user marks question for review
      setIsReviewingMode(false);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowNeedAnswerNotification(false); // Hide notification when jumping to specific question
    setIsReviewingMode(false); // Exit reviewing mode when jumping to specific question
  };

  // Helper function to find the next purple question (review later)
  const findNextReviewQuestion = (currentIndex, reviewList) => {
    // Find the next review question after currentIndex
    const nextReviewQuestions = reviewList.filter((q) => q > currentIndex);
    if (nextReviewQuestions.length > 0) {
      return Math.min(...nextReviewQuestions);
    }
    
    // If no review questions after current, find the first one overall
    const allReviewQuestions = reviewList.filter((q) => q !== currentIndex);
    if (allReviewQuestions.length > 0) {
      return Math.min(...allReviewQuestions);
    }
    
    // If no more review questions, return last question
    return questions.length - 1;
  };

  const finishTest = () => {
    const answeredCount = answers.filter(
      (a) => a !== undefined && a !== null
    ).length;
    if (answeredCount < questions.length) {
      setShowIncompleteModal(true);
      return;
    }
    
    // Check if there are questions marked for review
    if (reviewLaterQuestions.length > 0) {
      setShowReviewModal(true);
      return;
    }
    
    calculateResult();
  };

  const handleForceSubmit = () => {
    setShowReviewModal(false);
    setIsReviewingMode(false); // Exit reviewing mode when force submit
    calculateResult();
  };

  const handleGoToReview = () => {
    setShowReviewModal(false);
    setIsReviewingMode(true); // Enable reviewing mode
    // Go to the first review question (smallest index)
    const firstReviewQuestion = Math.min(...reviewLaterQuestions);
    setCurrentQuestion(firstReviewQuestion);
  };

  const selectAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
    
    // Hide notification if it was showing
    setShowNeedAnswerNotification(false);
    
    // Handle reviewing mode logic
    if (isReviewingMode) {
      // Remove current question from review list (turn green)
      const newReviewLater = reviewLaterQuestions.filter(
        (q) => q !== currentQuestion
      );
      setReviewLaterQuestions(newReviewLater);
      
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(
            currentQuestion,
            newReviewLater
          );
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode - Auto-advance logic - chỉ đi sequential, không đi đến review questions
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          // Go to next sequential question
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Go to last question for submit
          setCurrentQuestion(questions.length - 1);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    }
  };

  const calculateResult = () => {
    const scores = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
    const counts = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
    
    // Calculate scores based on activity types and ratings
    answers.forEach((rating, index) => {
      if (rating !== undefined && rating !== null) {
        const activityType = questions[index]?.type;
        if (scores.hasOwnProperty(activityType)) {
          scores[activityType] += rating;
          counts[activityType]++;
        }
      }
    });
    
    // Calculate average scores for each type
    const averageScores = {};
    Object.keys(scores).forEach((type) => {
      averageScores[type] =
        counts[type] > 0 ? Math.round((scores[type] / counts[type]) * 20) : 0; // Convert to 100 scale
    });
    
    // Find dominant type (highest average score)
    const dominantType = Object.keys(averageScores).reduce((a, b) => 
      averageScores[a] > averageScores[b] ? a : b
    );
    
    // Find top 3 types for Holland Code
    const sortedTypes = Object.keys(averageScores)
      .sort((a, b) => averageScores[b] - averageScores[a])
      .slice(0, 3);
    
    const hollandCode = sortedTypes.join("");
    
    const detailsString = `Phân tích chi tiết Holland Codes (RIASEC):
• Mã Holland: ${hollandCode}
• Loại chính: ${getTypeDescription(dominantType).name}
• Điểm trung bình:
  - Realistic (R): ${averageScores.R}/100
  - Investigative (I): ${averageScores.I}/100
  - Artistic (A): ${averageScores.A}/100
  - Social (S): ${averageScores.S}/100
  - Enterprising (E): ${averageScores.E}/100
  - Conventional (C): ${averageScores.C}/100
• Top 3 loại: ${sortedTypes
      .map((type) => getTypeDescription(type).name)
      .join(", ")}`;

    // Navigate to test result page with data
    navigate("/test-result", {
      state: {
        testType: "holland",
        result: hollandCode,
        details: getTypeDescription(dominantType).description,
        breakdown: detailsString,
      },
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setCurrentScreen("result");
      setPasswordError("");

      // Track password verification
      await trackPasswordVerification('holland');
    } else {
      setPasswordError("Mật khẩu không chính xác!");
    }
  };

  const getTypeDescription = (type) => {
    const descriptions = {
      R: {
        name: "Realistic (Thực tế)",
        color: "text-brown-600 bg-brown-50",
        traits: ["Thực tế", "Kỹ thuật", "Cụ thể", "Hoạt động"],
        description:
          "Bạn thích làm việc với đôi tay, máy móc, công cụ. Bạn thường thực tế, có tính cách ổn định và thích môi trường có cấu trúc.",
        careers: [
          "Kỹ sư",
          "Thợ sửa chữa",
          "Nông dân",
          "Thợ mộc",
          "Phi công",
          "Thú y",
        ],
        characteristics: [
          "Thích làm việc với tay",
          "Có kỹ năng cơ khí",
          "Thích hoạt động ngoài trời",
          "Thiên về thực tế",
        ],
      },
      I: {
        name: "Investigative (Nghiên cứu)",
        color: "text-purple-600 bg-purple-50",
        traits: ["Phân tích", "Tò mò", "Logic", "Khoa học"],
        description:
          "Bạn thích nghiên cứu, phân tích và giải quyết vấn đề. Bạn có xu hướng tư duy logic và thích khám phá tri thức.",
        careers: [
          "Nhà khoa học",
          "Bác sĩ",
          "Kỹ sư phần mềm",
          "Nhà nghiên cứu",
          "Dược sĩ",
          "Nhà toán học",
        ],
        characteristics: [
          "Thích giải quyết vấn đề",
          "Tư duy phân tích",
          "Yêu thích khoa học",
          "Học hỏi suốt đời",
        ],
      },
      A: {
        name: "Artistic (Nghệ thuật)",
        color: "text-pink-600 bg-pink-50",
        traits: ["Sáng tạo", "Biểu cảm", "Độc đáo", "Tự do"],
        description:
          "Bạn có khả năng sáng tạo cao, thích biểu đạt bản thân qua nghệ thuật. Bạn đánh giá cao vẻ đẹp và tính độc đáo.",
        careers: [
          "Họa sĩ",
          "Nhạc sĩ",
          "Nhà thiết kế",
          "Diễn viên",
          "Nhà văn",
          "Nhiếp ảnh gia",
        ],
        characteristics: [
          "Có khiếu thẩm mỹ",
          "Thích sự tự do",
          "Biểu đạt cảm xúc",
          "Sáng tạo và đổi mới",
        ],
      },
      S: {
        name: "Social (Xã hội)",
        color: "text-green-600 bg-green-50",
        traits: ["Quan tâm", "Giúp đỡ", "Giao tiếp", "Hợp tác"],
        description:
          "Bạn thích làm việc với con người, giúp đỡ và phát triển người khác. Bạn có kỹ năng giao tiếp tốt.",
        careers: [
          "Giáo viên",
          "Tư vấn viên",
          "Y tá",
          "Nhân viên xã hội",
          "Huấn luyện viên",
          "Nhà tâm lý",
        ],
        characteristics: [
          "Thích giúp đỡ người khác",
          "Kỹ năng giao tiếp tốt",
          "Quan tâm xã hội",
          "Làm việc nhóm hiệu quả",
        ],
      },
      E: {
        name: "Enterprising (Kinh doanh)",
        color: "text-orange-600 bg-orange-50",
        traits: ["Lãnh đạo", "Thuyết phục", "Cạnh tranh", "Mục tiêu"],
        description:
          "Bạn thích lãnh đạo, ảnh hưởng và thuyết phục người khác. Bạn có tham vọng và định hướng thành công.",
        careers: [
          "Nhà quản lý",
          "Doanh nhân",
          "Luật sư",
          "Nhân viên bán hàng",
          "Chính trị gia",
          "Nhà tiếp thị",
        ],
        characteristics: [
          "Khả năng lãnh đạo",
          "Định hướng mục tiêu",
          "Thích cạnh tranh",
          "Kỹ năng thuyết phục",
        ],
      },
      C: {
        name: "Conventional (Truyền thống)",
        color: "text-blue-600 bg-blue-50",
        traits: ["Tổ chức", "Chi tiết", "Chính xác", "Hệ thống"],
        description:
          "Bạn thích làm việc có tổ chức, chi tiết và chính xác. Bạn thích môi trường có cấu trúc và quy trình rõ ràng.",
        careers: [
          "Kế toán",
          "Thư ký",
          "Ngân hàng",
          "Kiểm toán",
          "Quản lý dữ liệu",
          "Hành chính",
        ],
        characteristics: [
          "Chú ý đến chi tiết",
          "Tổ chức tốt",
          "Tuân thủ quy trình",
          "Làm việc có hệ thống",
        ],
      },
    };
    return descriptions[type];
  };

  const getQuestionStatus = (index) => {
    if (reviewLaterQuestions.includes(index)) {
      return answers[index] !== undefined && answers[index] !== null
        ? "completed-review"
        : "review";
    }
    if (answers[index] !== undefined && answers[index] !== null)
      return "completed";
    return "unanswered";
  };

  const getQuestionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500 text-white";
      case "review":
        return "bg-purple-500 text-white";
      case "completed-review":
        return "bg-purple-500 text-white";
      default:
        return "bg-white border-2 border-gray-300 text-gray-700";
    }
  };

  const getRatingText = (rating) => {
    const texts = {
      1: "Rất không thích",
      2: "Không thích",
      3: "Trung lập",
      4: "Thích",
      5: "Rất thích",
    };
    return texts[rating] || "";
  };

  if (currentScreen === "intro") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 relative">
        {/* Subtle background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-emerald-100 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-teal-100 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-medium rounded-full mb-6">
                ✨ Test nghề nghiệp chính thức
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Test <span className="text-emerald-600">Holland Codes</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Khám phá tính cách nghề nghiệp và con đường sự nghiệp phù hợp
                với bạn
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">
                Thông tin bài Test Holland Codes chuẩn Quốc Tế
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">
                    {questions.length}
                  </div>
                  <div className="text-emerald-700 font-medium">Hoạt động</div>
                </div>
                
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">∞</div>
                  <div className="text-teal-700 font-medium">
                    Không giới hạn
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    6
                  </div>
                  <div className="text-green-700 font-medium">Nhóm RIASEC</div>
                </div>
              </div>
              
              {/* CTA Button */}
              <button 
                onClick={startTest}
                className="group relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mx-auto"
              >
                <span className="flex items-center justify-center">
                  Bắt đầu làm bài ngay
                </span>
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                Miễn phí • Không cần đăng ký • Kết quả tức thì
              </p>
            </div>

            {/* Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-left">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">
                  Lưu ý quan trọng
                </h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-emerald-700 mb-3">
                    Đánh giá thật lòng
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Đánh giá mức độ yêu thích từ 1 đến 5 theo cảm nhận thực tế
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-teal-700 mb-3">
                    Không có đúng sai
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Không có câu trả lời đúng hay sai, chỉ có sở thích cá nhân
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-green-700 mb-3">
                    Chân thành với bản thân
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Trả lời theo sở thích thật sự, không theo kỳ vọng người khác
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modern question board component
  const ModernQuestionBoard = () => (
    <div className="bg-white rounded-xl shadow-lg px-5 py-5 sticky top-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-800 text-center">
          Bảng theo dõi
        </h3>
      </div>
      
      <div className="grid grid-cols-7 gap-2 mb-5">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
              index === currentQuestion 
                ? "ring-2 ring-blue-400 ring-offset-1 " +
                  getQuestionStatusColor(getQuestionStatus(index))
                : getQuestionStatusColor(getQuestionStatus(index))
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
      
      <button
        onClick={finishTest}
        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm mb-5"
      >
        NỘP BÀI
      </button>
      
      <div className="grid grid-cols-3 gap-1 text-xs">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Đã làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-full mb-1"></div>
          <span className="text-gray-600">Chưa làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Cần kiểm tra</span>
        </div>
      </div>
    </div>
  );

  if (currentScreen === "test") {
    // Don't render if questions not loaded yet
    if (isLoading || questions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600">
              🔄 Đang tải câu hỏi Holland từ database...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Vui lòng chờ trong giây lát
            </p>
          </div>
        </div>
      );
    }

    const answeredCount = answers.filter(
      (a) => a !== undefined && a !== null
    ).length;
    const progress = (answeredCount / questions.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-5 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                        Test Holland Codes chuẩn RIASEC
                      </h2>
                      {isReviewingMode && (
                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                          🔍 Đang kiểm tra lại
                        </div>
                      )}
                    </div>
                    <div className="bg-emerald-100 text-emerald-600 px-4 py-2 rounded-full text-base font-bold">
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div 
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>
                      Tiến độ: {answeredCount} / {questions.length}
                    </span>
                    <span>Câu hiện tại: {currentQuestion + 1}</span>
                  </div>
                </div>

                {/* Mobile Progress Grid */}
                <div className="bg-white rounded-lg shadow-lg p-3 mb-4 xl:hidden">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800 text-sm">
                      Bảng theo dõi
                    </h3>
                    <button
                      onClick={finishTest}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2 px-4 rounded-lg text-sm"
                    >
                      NỘP BÀI
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-10 gap-1 mb-3">
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToQuestion(index)}
                        className={`w-7 h-7 rounded-full text-xs font-medium transition-all duration-200 ${
                          index === currentQuestion 
                            ? "ring-2 ring-blue-400 " +
                              getQuestionStatusColor(getQuestionStatus(index))
                            : getQuestionStatusColor(getQuestionStatus(index))
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded mr-1"></div>
                      <span>Đã làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-white border border-gray-300 rounded mr-1"></div>
                      <span>Chưa làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded mr-1"></div>
                      <span>Cần kiểm tra</span>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="bg-white rounded-lg shadow-lg p-5 mb-4">
                  <div className="flex items-center mb-6">
                    <span className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-sm font-semibold mr-4">
                      {currentQuestion + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800">
                      Mức độ yêu thích với hoạt động:
                    </h3>
                  </div>
                  
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-6">
                    <h3 className="text-2xl font-bold text-center text-gray-800">
                      {(() => {
                        const currentQ = questions[currentQuestion];
                        console.log("Holland Current Question:", currentQ);
                        console.log(
                          "Holland Current Question Index:",
                          currentQuestion
                        );
                        console.log("Holland Questions Array:", questions);
                        return (
                          currentQ?.activity ||
                          currentQ?.question ||
                          "Không có hoạt động"
                        );
                      })()}
                    </h3>
                  </div>
                  
                                      <div className="space-y-6">
                      <p className="text-center text-gray-600 text-lg mb-6">
                      Vui lòng đánh giá mức độ yêu thích của bạn với hoạt động
                      này:
                      </p>
                      
                      {/* Rating scale */}
                      <div className="flex items-center justify-center space-x-4 mb-4">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            onClick={() => {
                              selectAnswer(rating);
                              handleAnswer(rating);
                            }}
                            className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold transition-all duration-200 ${
                              answers[currentQuestion] === rating
                              ? "bg-emerald-500 text-white shadow-lg transform scale-110"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300 hover:transform hover:scale-105"
                            }`}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                      
                      {/* Rating labels */}
                      <div className="flex justify-between text-sm text-gray-600 px-8">
                        <span>Không thích</span>
                        <span>Bình thường</span>
                        <span>Rất thích</span>
                      </div>
                    </div>
                  
                  {/* Review Later Button Area - Mobile Responsive */}
                  <div className="mt-8 space-y-4">
                    {/* Review Later Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleReviewLater}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          reviewLaterQuestions.includes(currentQuestion)
                            ? "bg-purple-500 text-white hover:bg-purple-600"
                            : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        }`}
                      >
                        {reviewLaterQuestions.includes(currentQuestion)
                          ? "Bỏ đánh dấu"
                          : "Lát kiểm tra lại"}
                      </button>
                    </div>
                    
                    {/* Answer Required Notification - Mobile Responsive */}
                    {showNeedAnswerNotification && (
                      <div className="flex justify-center px-4">
                        <div className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md border border-blue-700 text-center max-w-full">
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium break-words">
                            Vui lòng chọn trước một đáp án để tiếp tục!
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="bg-white rounded-lg shadow-lg p-4 sticky bottom-4 z-10">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                    >
                      ← Câu trước
                    </button>

                    <div className="flex gap-3">
                      {isAutoAdvancing ? (
                        <div className="px-4 py-2 bg-emerald-100 text-emerald-600 rounded-lg text-sm font-medium flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-emerald-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Đang chuyển...
                        </div>
                      ) : currentQuestion === questions.length - 1 ? (
                        <button
                          onClick={finishTest}
                          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 text-sm"
                        >
                          Hoàn thành
                        </button>
                      ) : (
                        <button
                          onClick={nextQuestion}
                          className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 font-medium transition-all duration-200 text-sm"
                        >
                          Câu tiếp →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Question Board - Desktop Only */}
              <div className="hidden xl:block">
                <ModernQuestionBoard />
              </div>
            </div>
          </div>
        </div>

        {/* Incomplete Modal */}
        {showIncompleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                  <svg
                    className="h-6 w-6 text-orange-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Bài test chưa hoàn thành!
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn còn{" "}
                  {
                    questions.filter(
                      (_, i) => answers[i] === undefined || answers[i] === null
                    ).length
                  }{" "}
                  câu chưa trả lời. Bạn có muốn nộp bài ngay?
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowIncompleteModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Quay lại
                  </button>
                  <button
                    onClick={handleForceSubmit}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    Nộp bài ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012-2"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Còn câu hỏi cần kiểm tra lại
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có {reviewLaterQuestions.length} câu hỏi được đánh dấu cần
                  kiểm tra lại. Bạn có muốn xem lại những câu này trước khi nộp
                  bài không?
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                    onClick={handleGoToReview}
                  >
                    Xem lại câu hỏi
                  </button>
                  <button
                    type="button"
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
                    onClick={handleForceSubmit}
                  >
                    Nộp bài ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (currentScreen === "completed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                🎉 Chúc mừng! Bạn đã hoàn thành bài test!
              </h1>
              <p className="text-lg text-gray-600">
                Nhập mật khẩu để xem kết quả phân tích tính cách nghề nghiệp
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="mb-8">
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập mật khẩu để xem kết quả"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-sm mb-4">{passwordError}</p>
              )}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
              >
                Xem kết quả
              </button>
            </form>

            <button
              onClick={() => navigate("/")}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Quay về trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === "result" && hollandResult) {
    const typeInfo = getTypeDescription(hollandResult.dominantType);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                🌟 Kết quả Test Holland Codes
              </h1>
              <p className="text-lg text-gray-600">
                Phân tích tính cách nghề nghiệp và con đường sự nghiệp phù hợp
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className={`rounded-xl p-6 ${typeInfo.color}`}>
                <div className="text-center">
                  <div className="text-6xl font-bold mb-2">
                    {hollandResult.dominantType}
                  </div>
                  <div className="text-xl font-semibold">{typeInfo.name}</div>
                  <div className="text-lg mt-2">
                    {hollandResult.percentages[hollandResult.dominantType]}% ưu
                    thế
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(hollandResult.percentages)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, percentage]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    >
                    <div className="flex items-center">
                      <span className="text-2xl font-bold mr-3">{type}</span>
                        <span className="text-sm text-gray-600">
                          {getTypeDescription(type).name}
                        </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                        <span className="text-lg font-semibold">
                          {percentage}%
                        </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                📊 Phân tích tính cách chủ đạo: {typeInfo.name}
              </h2>
              <p className="text-gray-700 mb-4">{typeInfo.description}</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-green-800 mb-3">
                    ✅ Đặc điểm:
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-green-700">
                    {typeInfo.characteristics.map((char, index) => (
                      <li key={index}>{char}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-blue-800 mb-3">
                    💼 Nghề nghiệp phù hợp:
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                    {typeInfo.careers.map((career, index) => (
                      <li key={index}>{career}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">
                💡 Đặc điểm tính cách:
              </h2>
              <div className="flex flex-wrap gap-2">
                {typeInfo.traits.map((trait, index) => (
                  <span
                    key={index}
                    className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 mr-4"
              >
                Làm lại bài test
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-300 transition-all duration-200"
              >
                Quay về trang chủ
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default HollandCodes; 
